import {
  Box,
  CircularProgress,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useState } from "react";
import { SortType } from "../../../../../models/SortType";
import VendorTable from "../../components/vendor/VendorTable";
import { useStores } from "../../../use-stores";
import { vendor_type_list } from "../../../../../models/Vendors/VendorType";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { useLocation, useNavigate } from "react-router-dom";
import { GetVendorsParams } from "../../../../../models/Vendors/Vendor";
import { FilterItem } from "../../../../../models/Filters/FilterValue";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { ExpandLess, ExpandMore, AddOutlined } from "@mui/icons-material";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";

interface Filters {
  search_text: String | null;
  type: FilterItem[];
}

const Vendor: React.FC = () => {
  const theme = useTheme();

  const { vendorStore, userStore } = useStores();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { showError } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const [vendorTypeFilter, setVendorTypeFilter] = useState<number[]>([]);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const getVendorType = (id: number) => {
    const vendorType = vendor_type_list.find(
      (vendor) => vendor.type_id.toString() === id.toString(),
    );
    return vendorType ? vendorType.type : "";
  };
  const [filters, setFilters] = useState<Filters>({
    search_text: searchParams.get("search_text")
      ? searchParams.get("search_text")
      : "",
    type: searchParams.get("type")
      ? searchParams
          .get("type")!
          .split(",")
          .map(
            (item) =>
              ({
                id: Number(item),
                name: getVendorType(Number(item)),
              }) as FilterItem,
          )
      : [],
  });

  const [params, setParams] = useState<GetVendorsParams>({
    page_no: 1,
    limit: 30,
    sortBy: "id",
    sortType: SortType.DESC,
    search_text: "",
    type: filters?.type ? filters.type.map((item) => item.id) : [],
  });

  useEffect(() => {
    if (userStore.hasPermission(EmployeeRoles.VIEW_VENDORS)) {
      setLoading(true);
      getVendors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, userStore]);

  useEffect(() => {
    const searchText = searchParams.get("search_text");
    if (searchText) {
      setParams((prev) => ({
        ...prev,
        search_text: searchText.trim(),
      }));
      setSearchTerm(searchText.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const updateQueryParams = () => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value && (!Array.isArray(value) || value.length > 0)) {
          const paramValue = Array.isArray(value)
            ? value.join(",")
            : value.toString();
          searchParams.set(key, paramValue);
        }
      });

      const queryString = searchParams.toString();
      if (queryString) {
        navigate({ search: queryString }, { replace: true });
      }
    };

    updateQueryParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const getVendors = () => {
    vendorStore
      .getVendors(params)
      .then(() => {
        setLoading(false);
      })
      .catch((err) => {});
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setParams((prev) => ({ ...prev, page_no: newPage }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev) => ({ ...prev, limit: newLimit, page_no: 1 }));
  };

  const handleSortChange = (newSortBy: string) => {
    setParams((prevParams) => ({
      ...prevParams,
      sortBy: newSortBy,
      sortType:
        prevParams.sortType === SortType.ASC ? SortType.DESC : SortType.ASC,
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      if (searchTerm.trim().length > 2) {
        setParams((prev) => ({
          ...prev,
          search_text: searchTerm.trim(),
        }));
      } else {
        showError("Error", "Minimum character length should be 3");
      }
    }
  };

  const handleSearchBar = async (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (e.target.value.length !== 0) {
      setSearchTerm(e.target.value);
    } else {
      setSearchTerm("");
      setParams((prev) => ({
        ...prev,
        search_text: "",
      }));
    }
  };

  const handleVendorTypeFilter = (updatedFilters: any) => {
    const Ids = updatedFilters.type.map((item: any) => item.id);
    setVendorTypeFilter(Ids);
    setFilters((prev) => ({
      ...prev,
      ...updatedFilters,
    }));
  };

  const handleFilterApply = () => {
    setParams((prevState) => ({
      ...prevState,
      type: vendorTypeFilter,
    }));
    setFilters({
      search_text: searchTerm.trim(),
      type: vendorTypeFilter?.map((typeId) => ({
        id: typeId,
        name: getVendorType(typeId),
      })),
    });
  };

  const clearFilters = () => {
    setSearchTerm("");
    setParams((prevState) => ({
      ...prevState,
      search_text: "",
      type: [],
    }));
    setFilters({
      search_text: "",
      type: [],
    });
  };

  const handleSearchBtn = () => {
    if (searchTerm.trim().length > 2) {
      setParams((prev) => ({
        ...prev,
        search_text: searchTerm.trim(),
      }));
    } else {
      showError("Error", "Minimum character length should be 3");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  // const throttledSearch = useCallback(throttle(handleSearchBtn, 2000), [
  //   searchTerm,
  // ]);

  function throttle(func: (...args: any[]) => void, delay: number) {
    let lastExecutedTime = 0;

    return function (...args: any) {
      const now = Date.now();
      if (now - lastExecutedTime >= delay) {
        lastExecutedTime = now;
        func(...args);
      }
    };
  }

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      {!userStore.hasPermission([
        EmployeeRoles.VIEW_VENDORS,
        EmployeeRoles.EDIT_VENDORS,
      ]) && (
        <Paper>
          <Box
            sx={{
              marginTop: "0.6rem",
              paddingX: 4,
              paddingY: 2,
              boxShadow:
                "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
              borderRadius: "4px",
              minHeight: { xs: "60vh", md: window.innerHeight - 110 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.4,
                mb: 2,
              }}
            >
              <CustomTypography variant="heading3">Invoices</CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                View and manage invoices. Search by customer name, order ID, or
                invoice number.
              </CustomTypography>
            </Box>
            <PermissionBlockLabel />
          </Box>
        </Paper>
      )}
      {userStore.hasPermission([
        EmployeeRoles.VIEW_VENDORS,
        EmployeeRoles.EDIT_VENDORS,
      ]) && (
        <Paper>
          <Box
            sx={{
              marginTop: "0.6rem",
              paddingX: 4,
              paddingY: 2,
              boxShadow:
                "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
              borderRadius: "4px",
              minHeight: { xs: "60vh", md: window.innerHeight - 110 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  gap: 0.5,
                }}
              >
                <CustomTypography variant="heading3">Vendors</CustomTypography>
                <CustomTypography
                  variant="body2"
                  color={theme.palette.customColors?.greyTextColor}
                >
                  View and manage vendors. Search by vendor name and vendor
                  type.
                </CustomTypography>
              </Box>
              {userStore.hasPermission(EmployeeRoles.EDIT_VENDORS) && (
                <AppButton
                  startIcon={<AddOutlined />}
                  variant="primary"
                  label="Add Vendor"
                  onClick={() => {
                    navigate("/vendors/add-edit-vendor");
                  }}
                  customStyles={{ width: "140px" }}
                />
              )}
            </Box>

            <Paper
              variant="outlined"
              sx={{
                mt: 2,
                borderRadius: 2,
                borderColor: "divider",
                overflow: "hidden",
              }}
            >
              <Box
                onClick={() => setIsSearchExpanded(!isSearchExpanded)}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                  py: 0.5,
                  cursor: "pointer",
                  borderBottom: isSearchExpanded ? "1px solid" : "none",
                  borderColor: "divider",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <CustomTypography variant="heading6">Search</CustomTypography>
                <IconButton size="small">
                  {isSearchExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={isSearchExpanded} timeout="auto" unmountOnExit>
                <Grid2 container spacing={2} columns={12} padding={2}>
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                    <CustomTextField
                      width={"100%"}
                      placeholder="Search by vendor name"
                      value={searchTerm}
                      onChange={(e) => {
                        handleSearchBar(e);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleKeyDown(e);
                        }
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <AppSearchDropdownMulti
                      title="Vendor type"
                      isMultiSelect={true}
                      value={filters.type.map((item) => ({
                        id: item.id.toString(),
                        value: item.name,
                      }))}
                      list={
                        vendor_type_list?.map((vendorType) => ({
                          id: vendorType.type_id.toString(),
                          value: vendorType.type,
                        })) || []
                      }
                      local={false}
                      handleSelect={(selectedVendorType) =>
                        handleVendorTypeFilter({
                          type: selectedVendorType?.map((vendor) => ({
                            id: Number(vendor.id),
                            name: getVendorType(Number(vendor.id)),
                          })),
                        })
                      }
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}></Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}></Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <AppButton
                        variant="transparentPrimary"
                        label="Clear"
                        onClick={clearFilters}
                        customStyles={{ width: "100%" }}
                      />
                      <AppButton
                        variant="primary"
                        label="Apply"
                        onClick={handleFilterApply}
                        customStyles={{ width: "100%" }}
                      />
                    </Box>
                  </Grid2>
                </Grid2>
              </Collapse>
            </Paper>

            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "50vh",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <VendorTable
                vendors={vendorStore.vendors}
                maxHeight={window.innerHeight - 350}
                limit={params.limit || vendorStore.limit}
                page_no={params.page_no}
                total_count={vendorStore.total_count}
                total_pages={vendorStore.total_pages}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                onSortChange={handleSortChange}
                sortBy={params.sortBy!}
                sortType={params.sortType!}
              />
            )}
          </Box>
        </Paper>
      )}
    </Grid2>
  );
};

export default observer(Vendor);
